import { ChangeDetectionStrategy, Component, ElementRef, effect, input, output, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { BetSlipGroupViewModel, SelectionToggleRequest } from '../sportsbook/sportsbook.models';
import { PulseOnChangeDirective } from '../shared/pulse-on-change.directive';
import {
  BET_SLIP_IDLE_SUBMISSION_STATE,
  BetSlipMode,
  BetSlipSubmissionState,
  BetSlipSummaryViewModel
} from './betslip-ticket.models';

@Component({
  selector: 'app-betslip',
  standalone: true,
  imports: [CommonModule, TranslateModule, PulseOnChangeDirective],
  templateUrl: './betslip.component.html',
  styleUrl: './betslip.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BetSlipComponent {
  readonly expanded = input.required<boolean>();
  readonly pulseMaxWin = input(false);
  readonly mobileDeleteConfirm = input(false);
  readonly showBackButton = input(false);
  readonly groups = input.required<readonly BetSlipGroupViewModel[]>();
  readonly summary = input.required<BetSlipSummaryViewModel>();
  readonly submissionState = input<BetSlipSubmissionState>(BET_SLIP_IDLE_SUBMISSION_STATE);
  readonly maxWinPulseKey = input.required<number>();

  readonly toggleExpanded = output<void>();
  readonly selectionToggle = output<SelectionToggleRequest>();
  readonly stakeChange = output<number>();
  readonly modeChange = output<BetSlipMode>();
  readonly systemStep = output<number>();
  readonly clearBetSlip = output<void>();
  readonly placeBet = output<void>();
  private readonly pendingDeletionKey = signal<string | null>(null);
  private readonly emptyBackButton = viewChild<ElementRef<HTMLButtonElement>>('emptyBackButton');

  constructor() {
    effect(() => {
      // Removing the last tip also removes its focused control.
      this.emptyBackButton()?.nativeElement.focus({ preventScroll: true });
    });
  }

  protected onStakeInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const parsedValue = Number.parseFloat(target.value);
    this.stakeChange.emit(Number.isNaN(parsedValue) ? 0 : parsedValue);
  }

  protected onCollapsedSubmitClick(): void {
    if (this.summary().primaryActionEnabled) {
      this.placeBet.emit();
    }
  }

  protected onSelectionInteraction(
    event: MouseEvent,
    group: BetSlipGroupViewModel,
    selection: BetSlipGroupViewModel['selections'][number]
  ): void {
    if (!this.mobileDeleteConfirm()) {
      this.emitSelectionToggle(group, selection);
      return;
    }

    if (this.pendingDeletionKey() !== selection.key) {
      this.pendingDeletionKey.set(selection.key);
      return;
    }

    const selectionElement = event.currentTarget as HTMLElement;
    const bounds = selectionElement.getBoundingClientRect();
    const clickOffset = event.clientX - bounds.left;

    if (clickOffset <= bounds.width / 2) {
      this.pendingDeletionKey.set(null);
      this.emitSelectionToggle(group, selection);
      return;
    }

    this.pendingDeletionKey.set(null);
  }

  protected isSelectionDeleteArmed(selectionKey: string): boolean {
    return this.pendingDeletionKey() === selectionKey;
  }

  protected activeNoticeKey(): string | null {
    return this.submissionState().messageKey ?? this.summary().blockingMessageKey;
  }

  protected activeNoticeParams(): Record<string, string | number> | undefined {
    return this.submissionState().messageParams ?? this.summary().blockingMessageParams;
  }

  protected readonly trackGroup = (_: number, group: BetSlipGroupViewModel): number => group.matchId;
  protected readonly trackSelection = (_: number, selection: BetSlipGroupViewModel['selections'][number]): string => selection.key;

  private emitSelectionToggle(
    group: BetSlipGroupViewModel,
    selection: BetSlipGroupViewModel['selections'][number]
  ): void {
    this.selectionToggle.emit({
      matchId: group.matchId,
      betTypeId: selection.betTypeId,
      oddId: selection.oddId
    });
  }
}
